import { fetchJSON as fetchAPI } from './client';

export type AdsDashboard = {
  total_spend: number;
  total_sales: number;
  overall_roas: number;
  overall_acos: number;
  campaign_health_score: number;
  budget_health_score: number;
  keyword_health_score: number;
  active_campaigns: number;
  active_recommendations: number;
  opportunity_score: number;
  savings_potential: number;
  growth_potential: number;
  running_experiments: number;
};

export type CampaignSummary = {
  id: string;
  name: string;
  type: string;
  status: string;
  daily_budget: number;
  health: string;
  opportunity_score: number;
  metrics: PerformanceMetrics;
};

export type PerformanceMetrics = {
  impressions: number;
  clicks: number;
  ctr: number;
  spend: number;
  sales: number;
  orders: number;
  roas: number;
  acos: number;
  cpc: number;
  cvr: number;
};

export type KeywordIntel = {
  id: string;
  keyword: string;
  match_type: string;
  bid: number;
  suggested_bid?: number;
  cluster?: string;
  intent?: string;
  recommendation?: string;
  metrics: PerformanceMetrics;
};

export type SearchTermIntel = {
  id: string;
  search_term: string;
  classification?: string;
  action?: string;
  metrics: PerformanceMetrics;
};

export type AdRecommendation = {
  id: string;
  type: string;
  status: string;
  title: string;
  description?: string;
  evidence?: string;
  expected_savings?: number;
  expected_growth?: number;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

export type BudgetForecast = {
  id: string;
  period: string;
  expected_spend: number;
  expected_sales: number;
  expected_roas: number;
  growth_pct?: number;
};

export type ExperimentRecord = {
  id: string;
  name: string;
  type: string;
  status: string;
  hypothesis?: string;
  created_at: string;
};

export type ExecutionRecord = {
  id: string;
  recommendation_id?: string;
  status: string;
  title: string;
  workflow_id?: string;
  governance_approved: boolean;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_AMAZONADSINTEL_API ?? '/api/v1/amazonadsintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const amazonadsintelApi = {
  dashboard: () => fetchJSON<AdsDashboard>('/dashboard'),
  listCampaigns: () => fetchJSON<CampaignSummary[]>('/campaigns'),
  listKeywords: () => fetchJSON<KeywordIntel[]>('/keywords'),
  listSearchTerms: () => fetchJSON<SearchTermIntel[]>('/search-terms'),
  listRecommendations: () => fetchJSON<AdRecommendation[]>('/recommendations'),
  generateRecommendations: () => fetchJSON<AdRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<AdRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listForecasts: () => fetchJSON<BudgetForecast[]>('/forecasts'),
  listExperiments: () => fetchJSON<ExperimentRecord[]>('/experiments'),
  createExperiment: (body: { name: string; type: string; hypothesis?: string }) =>
    fetchJSON<ExperimentRecord>('/experiments', { method: 'POST', body: JSON.stringify(body) }),
  listExecutions: () => fetchJSON<ExecutionRecord[]>('/execution'),
  startExecution: (recommendationId: string) =>
    fetchJSON<ExecutionRecord>('/execution/start', { method: 'POST', body: JSON.stringify({ recommendation_id: recommendationId, use_workflow: true }) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
